import { useEffect, useRef, useCallback } from 'react';
import { trackFeedSourceDiversity } from '@/lib/analytics';

const STORAGE_KEY = 'mw_feed_sources';

/**
 * ANALYTICS-043: Feed source diversity tracking.
 * Tracks distinct article sources read in a session. Fires a
 * `feed_source_diversity` sendBeacon event when the user reads
 * articles from 3+ distinct sources, indicating broad feed engagement.
 * Fires once per session (gated by sessionStorage flag).
 */
export function useFeedSourceDiversity() {
  const firedRef = useRef(false);

  useEffect(() => {
    // Check if already fired this session
    if (typeof sessionStorage === 'undefined') return;
    try {
      if (sessionStorage.getItem('mw_source_diversity_fired') === '1') {
        firedRef.current = true;
      }
    } catch { /* ignore */ }
  }, []);

  const recordSource = useCallback((sourceName: string) => {
    if (firedRef.current || !sourceName || sourceName === 'unknown') return;
    if (typeof sessionStorage === 'undefined') return;

    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const sources: string[] = raw ? JSON.parse(raw) : [];
      if (!sources.includes(sourceName)) {
        sources.push(sourceName);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sources));
      }

      if (sources.length >= 3 && !firedRef.current) {
        firedRef.current = true;
        sessionStorage.setItem('mw_source_diversity_fired', '1');
        trackFeedSourceDiversity(sources.length, sources);
      }
    } catch { /* silently ignore */ }
  }, []);

  return { recordSource };
}
